import { useSnackbar } from 'notistack';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { useOrganization } from '@/context/organization-context';
import { useNavigate, createFileRoute } from '@tanstack/react-router';
import { IntegrationResponseDtoType } from '@/apis/backend/generated/models/integrationResponseDtoType';
import { IntegrationResponseDtoStatus } from '@/apis/backend/generated/models/integrationResponseDtoStatus';
import {
  hubspotOAuthControllerInstall,
  useIntegrationsControllerListIntegrations,
  useIntegrationsControllerDisconnectIntegration,
} from '@/apis/backend/generated/integrations/integrations';

import {
  Box,
  Card,
  Chip,
  Stack,
  Alert,
  Button,
  Skeleton,
  Typography,
  CardContent,
  CircularProgress,
} from '@mui/material';

import { paths } from 'src/routes/paths';
import { Iconify } from 'src/components/iconify';

export const Route = createFileRoute(
  '/_authed/dashboard/o/$orgId/settings/client/brand/$clientId/integrations/hubspot'
)({
  component: HubspotIntegrationPage,
});

function HubspotIntegrationPage() {
  const { t } = useTranslation('settings');
  const navigate = useNavigate();
  const { orgId: routeOrgId, clientId } = Route.useParams();
  const { activeOrganization, activeClient } = useOrganization();
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  const orgId = activeOrganization?.id || '';
  const activeClientId = activeClient?.id || '';

  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [isConnecting, setIsConnecting] = useState(false);

  // Surface the OAuth redirect result (?success / ?error) once, then strip the
  // params so a refresh doesn't re-show the banner.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const oauthError = params.get('error');
    const oauthSuccess = params.get('success');
    if (oauthError) setError(oauthError);
    if (oauthSuccess) {
      setSuccess(
        t('hubspot.connectSuccess', 'HubSpot connected successfully.')
      );
    }
    if (oauthError || oauthSuccess) {
      const url = new URL(window.location.href);
      url.searchParams.delete('error');
      url.searchParams.delete('success');
      window.history.replaceState({}, '', url.toString());
      queryClient.invalidateQueries({
        predicate: (query) => {
          const key = query.queryKey[0];
          return typeof key === 'string' && key.includes('/integrations');
        },
      });
    }
  }, [queryClient, t]);

  const { data: integrationsData, isLoading } =
    useIntegrationsControllerListIntegrations(orgId, activeClientId, {
      query: {
        enabled: !!orgId && !!activeClientId,
        staleTime: 30000,
      },
    });

  const hubspotIntegration = integrationsData?.integrations?.find(
    (i) => i.type === IntegrationResponseDtoType.hubspot
  );
  const isConnected =
    hubspotIntegration?.status === IntegrationResponseDtoStatus.active ||
    hubspotIntegration?.status === IntegrationResponseDtoStatus.syncing;

  // The `content` scope (Marketing/CMS Hub Pro/Enterprise only) gates
  // marketing-email creation. When absent, audiences + analytics still work,
  // so we connect fine but warn that template export is unavailable.
  const grantedScopes = (hubspotIntegration?.data as { grantedScopes?: string[] } | undefined)
    ?.grantedScopes;
  const hasContentScope = grantedScopes?.includes('content') ?? false;

  const disconnectMutation = useIntegrationsControllerDisconnectIntegration({
    mutation: {
      onSuccess: () => {
        enqueueSnackbar(t('hubspot.disconnectSuccess', 'HubSpot disconnected'), {
          variant: 'success',
        });
        queryClient.invalidateQueries({
          predicate: (query) => {
            const key = query.queryKey[0];
            return typeof key === 'string' && key.includes('/integrations');
          },
        });
      },
      onError: () => {
        enqueueSnackbar(t('hubspot.disconnectError', 'Failed to disconnect'), {
          variant: 'error',
        });
      },
    },
  });

  const handleConnect = async () => {
    if (!orgId || !activeClientId) {
      setError(t('hubspot.noClientOrOrg', 'No active client or organization selected.'));
      return;
    }
    try {
      setIsConnecting(true);
      setError(null);
      const response = await hubspotOAuthControllerInstall({
        clientId: activeClientId,
        orgId,
        source: 'settings',
      });
      if (response?.redirectUrl) {
        window.location.href = response.redirectUrl;
      } else {
        throw new Error('No redirect URL received from server');
      }
    } catch (err) {
      console.error('Failed to connect HubSpot:', err);
      setError(t('hubspot.connectError', 'Failed to initiate HubSpot connection. Please try again.'));
      enqueueSnackbar(t('hubspot.connectErrorSnackbar', 'Failed to connect HubSpot'), {
        variant: 'error',
      });
    } finally {
      setIsConnecting(false);
    }
  };

  const handleDisconnect = () => {
    if (!hubspotIntegration?.id) return;
    if (
      !window.confirm(
        t('hubspot.disconnectConfirm', 'Disconnect HubSpot? Exported emails remain in HubSpot.')
      )
    )
      return;
    disconnectMutation.mutate({ orgId, clientId: activeClientId, id: hubspotIntegration.id });
  };

  return (
    <Box>
      <Button
        size="small"
        startIcon={<Iconify icon="eva:arrow-back-fill" width={16} />}
        onClick={() =>
          navigate({
            to: paths.dashboard.orgSettings(routeOrgId).client.clientIntegrations(clientId),
          })
        }
        sx={{ mb: 2, textTransform: 'none' }}
      >
        {t('hubspot.backToIntegrations', 'Back to Integrations')}
      </Button>

      <Card>
        <CardContent>
          {isLoading ? (
            <Stack spacing={2}>
              <Skeleton variant="rounded" height={24} width="60%" />
              <Skeleton variant="rounded" height={18} width="40%" />
            </Stack>
          ) : (
            <Stack spacing={3}>
              <Stack direction="row" spacing={2} alignItems="center">
                <Box
                  component="img"
                  src="/logos/hubspot.svg"
                  alt="HubSpot"
                  sx={{ width: 40, height: 40 }}
                />
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h6">HubSpot</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('hubspot.headerSubtitle', 'Export templates and create marketing emails in HubSpot')}
                  </Typography>
                </Box>
                <Chip
                  label={
                    isConnected
                      ? t('hubspot.statusConnected', 'Connected')
                      : t('hubspot.statusNotConnected', 'Not Connected')
                  }
                  color={isConnected ? 'success' : 'default'}
                  size="small"
                  variant="outlined"
                />
              </Stack>

              {error && (
                <Alert severity="error" onClose={() => setError(null)}>
                  {error}
                </Alert>
              )}
              {success && (
                <Alert severity="success" onClose={() => setSuccess(null)}>
                  {success}
                </Alert>
              )}

              {!isConnected ? (
                <Stack spacing={2} alignItems="flex-start">
                  <Typography variant="body2" color="text.secondary">
                    {t(
                      'hubspot.notConnectedDescription',
                      'Connect your HubSpot account to export nexmail templates as marketing emails, sync contact lists, and subscribe signup-form contacts. Marketing-email creation requires a Marketing or CMS Hub Pro/Enterprise portal.'
                    )}
                  </Typography>
                  <Button
                    variant="contained"
                    onClick={handleConnect}
                    disabled={isConnecting}
                    startIcon={
                      isConnecting ? (
                        <CircularProgress size={20} color="inherit" />
                      ) : (
                        <Iconify icon="mdi:hubspot" width={20} />
                      )
                    }
                  >
                    {isConnecting
                      ? t('hubspot.connecting', 'Connecting...')
                      : t('hubspot.connectButton', 'Connect HubSpot')}
                  </Button>
                </Stack>
              ) : (
                <Stack spacing={2}>
                  {!hasContentScope && (
                    <Alert severity="info">
                      {t(
                        'hubspot.noContentScope',
                        'This HubSpot portal has not granted the "content" scope, so marketing-email creation is unavailable. Contact lists and analytics still sync. The content scope requires Marketing or CMS Hub Pro/Enterprise.'
                      )}
                    </Alert>
                  )}
                  <Box sx={{ pt: 1, borderTop: 1, borderColor: 'divider' }}>
                    <Button
                      color="error"
                      size="small"
                      onClick={handleDisconnect}
                      disabled={disconnectMutation.isPending}
                      startIcon={
                        disconnectMutation.isPending ? (
                          <CircularProgress size={16} color="inherit" />
                        ) : (
                          <Iconify icon="eva:close-circle-outline" width={16} />
                        )
                      }
                    >
                      {t('hubspot.disconnectButton', 'Disconnect HubSpot')}
                    </Button>
                  </Box>
                </Stack>
              )}
            </Stack>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}
